// A door is the same (u, v) opening stamp as a window, with sill = 0 (see
// backend/docs/TODO.md's "Doors" note): same Facet-region model, not a new
// geometry class. This module supplies only what's genuinely door-specific —
// a default placement and the per-surface lookup — every geometry primitive
// (clamp, overlap, translate, drag) is reused verbatim from `windowOpening.ts`,
// which is generic over `Omit<WindowEntry, 'id'>` and so accepts a
// `DoorEntry` with zero changes.

import type { DoorEntry, WindowEntry } from '@roomtreatment/core';
import {
  DOOR_DOUBLE_WIDTH_M,
  DOOR_STANDARD_HEIGHT_M,
  DOOR_STANDARD_WIDTH_M,
  syncDoorGeometry,
} from '@roomtreatment/core';
import type { EditorDoc } from '@roomtreatment/core';
import { clampWindowOpening, openingsOverlap } from './windowOpening';
import { surfaceKey, type SurfaceFrame, type SurfaceRef } from './panelSurface';

/**
 * Default door placement: standard (or double) width/height, centred on the
 * wall, sill on the floor. Steps outward along the wall to find a spot clear
 * of every existing window AND door on the same surface, mirroring
 * `defaultWindow`'s search loop.
 *
 * `existing` must carry every opening already on this wall — doors AND
 * windows — or a default door can silently land on top of a window that
 * happens to sit at the wall's centre (where both `defaultDoor` and
 * `defaultWindow` start their search). Callers: pass
 * `[...doorsOn(doc, frame.ref), ...windowsOn(doc, frame.ref)]`, never doors
 * alone.
 *
 * Only meaningful on a WALL surface — callers must not offer this on a
 * ceiling or floor frame (a door has no meaning there); this function does
 * not itself gate on `frame.ref.kind`, since the caller already knows which
 * frames it hands it.
 */
export function defaultDoor(
  frame: SurfaceFrame,
  existing: (DoorEntry | WindowEntry)[],
  variant: 'single' | 'double' = 'single',
): Omit<DoorEntry, 'id'> {
  const w = Math.min(variant === 'double' ? DOOR_DOUBLE_WIDTH_M : DOOR_STANDARD_WIDTH_M, frame.uMax);
  const h = Math.min(DOOR_STANDARD_HEIGHT_M, frame.vMax);
  const base = syncDoorGeometry({
    surface: surfaceKey(frame.ref),
    u: frame.uMax / 2,
    v: h / 2,
    w,
    h,
    variant,
  });
  const step = w + 0.4;
  for (let k = 0; k < 8; k++) {
    for (const dir of k === 0 ? [0] : [1, -1]) {
      const cand = clampWindowOpening(frame, {
        ...base,
        u: frame.uMax / 2 + dir * k * step,
      });
      if (!existing.some((e) => openingsOverlap(e, cand))) return cand;
    }
  }
  return clampWindowOpening(frame, { ...base, u: frame.uMax / 2 });
}

/** The doors on one surface, in its own (u, v) frame. */
export function doorsOn(doc: EditorDoc, ref: SurfaceRef): DoorEntry[] {
  const key = surfaceKey(ref);
  return (doc.doors ?? []).filter((d) => d.surface === key);
}
